import { AlertType } from "@shared/components/ui-kit/feedback/alerts/alert-types";
import { Toast as RNToast } from "react-native-toast-notifications";
import { INotifier } from "./INotifier";

/**
 * Displays a toast notification with onClose callback.
 *
 * @param message - The message to be displayed in the toast notification.
 * @param type - The type of the toast notification (error, success, info).
 * @param onClose - Callback to execute when toast is closed.
 */
export async function displayToast(
  message: string,
  type: AlertType,
  onClose?: () => void
): Promise<void> {
  const title = type.charAt(0).toUpperCase() + type.slice(1);
  // Ensure message is a string (might receive Error objects or other types)
  const formattedMessage =
    typeof message === "string" ? message : String(message ?? "");

  RNToast.show(title, {
    data: { description: formattedMessage },
    type,
    onClose,
  });
}

export class MobileToastNotifier implements INotifier {
  private activeToasts = new Set<string>();

  /**
   * Generate a consistent ID from a message for toast deduplication
   */
  private static generateToastId(message: string, type?: string): string {
    const content = `${type || ""}_${String(message || "")
      .trim()
      .toLowerCase()}`;

    // Simple hash function for generating consistent IDs
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      // eslint-disable-next-line no-bitwise
      hash = (hash << 5) - hash + char;
      // eslint-disable-next-line no-bitwise
      hash |= 0; // Convert to 32-bit integer
    }
    return `toast_${Math.abs(hash)}`;
  }

  /**
   * Add toast to active set
   */
  private addActiveToast(toastId: string): void {
    this.activeToasts.add(toastId);
  }

  /**
   * Remove toast from active set
   */
  private removeActiveToast(toastId: string): void {
    this.activeToasts.delete(toastId);
  }

  /**
   * Internal method to show toast with deduplication check
   */
  private async show(message: string, type: AlertType): Promise<void> {
    const toastId = MobileToastNotifier.generateToastId(message, type);

    // Check if this toast is already active
    if (this.activeToasts.has(toastId)) {
      return;
    }

    // Add to active toasts
    this.addActiveToast(toastId);

    // Show toast with onClose callback
    await displayToast(message, type, () => {
      this.removeActiveToast(toastId);
    });
  }

  public error = (message: string) => this.show(message, "error");
  public success = (message: string) => this.show(message, "success");
  public info = (message: string) => this.show(message, "info");
}
